<template>
	<StandardModal
		open
		large
		hideCancel
		:confirmText="t('common_button_close')"
		@confirm="emit('close')"
		class="poll-results-modal"
		ref="modal"
	>
		<template #title>
			<div class="poll-results-title-container">
				<div class="poll-results-title">{{ t('polls_results_title') }}</div>
				<PollCloseState :pollId="pollId" class="poll-results-subtitle" />
			</div>
		</template>

		<div class="poll-results-container">
			<div class="poll-question">
				<div
					class="poll-question-text-container"
					:class="{ translated: poll?.isTranslated }"
				>
					<span class="poll-question-text">
						{{ displayedQuestion }}
					</span>
					<span v-if="canTranslate">{{ '\xa0' }}</span>
					<TranslationGlobe
						v-if="canTranslate"
						class="poll-translation"
						:lang="poll?.language"
						:translated="poll?.isTranslated"
						@click="onGlobeClick"
					/>
				</div>
			</div>

			<Input
				v-if="!isAnonymous"
				v-model="query"
				type="text"
				:placeholder="t('common_search')"
				:icon="SearchIcon"
				:iconProps="{ type: 'regular' }"
				class="search-input"
			/>

			<div class="poll-results-options-container" v-if="!noResults">
				<div
					v-for="option in poll?.options"
					:key="option.id"
					class="poll-option-section"
					:class="{ hidden: query.length > 0 && option.voters.length === 0 }"
				>
					<Accordian
						v-if="!query || (query && option.voters.length > 0)"
						:isLoading="isLoading"
						class="poll-voters-accordian"
						:hideChevron="
							query.length > 0 || isAnonymous || option?.voters?.length === 0
						"
					>
						<template #title>
							<div class="poll-option-title-container">
								<div
									class="poll-option-title"
									:class="{ translated: isOptionTranslated(option) }"
								>
									{{ getDisplayedOptionText(option) }}
								</div>
							</div>
						</template>
						<template #description>
							<div class="vote-stats">
								<div class="vote-count">
									{{
										t(
											'polls_results_vote_count.variable',
											{ count: option.vote_count },
											option.vote_count
										)
									}}
								</div>
								<div class="vote-percentage">
									{{ getVotePercentage(option) }}%
								</div>
							</div>
						</template>
						<div class="poll-voters-list">
							<div
								v-for="voter in option.voters"
								:key="voter.id"
								class="voter-item"
								@click="onClickUser(voter)"
							>
								<ProfilePic :user="voter" size="32px" class="voter-profile" />
								<div class="voter-name" v-text="voter.fullname" />
							</div>
							<div
								class="voter-item view-all-votes"
								v-if="option.has_more"
								@click="openAllVotersModal(option)"
							>
								{{ t('polls_results_view_all_voters') }}
							</div>
						</div>
					</Accordian>
				</div>
			</div>
			<div v-else class="poll-results-no-results">
				<div>
					{{ t('polls_results_no_results') }}
				</div>
			</div>
		</div>

		<!-- All Voters Modal -->
		<StandardModal
			v-if="showAllVotersModal"
			open
			medium
			:title="getDisplayedOptionText(selectedOption)"
			:confirmText="t('common_button_close')"
			hideCancel
			@confirm="showAllVotersModal = false"
			@cancel="showAllVotersModal = false"
			class="all-voters-modal"
		>
			<div class="all-voters-container">
				<Table
					ref="table"
					:columns="votersColumns"
					:rows="allVoters"
					infinite
					hideHeader
					clickable
					@row-click="onClickUser"
					@load-more="loadMore"
					:loadingMore="loadingMoreVoters"
					:loadingOverlay="isLoading"
					loadMoreTrigger="50px"
					height="532px"
				>
					<template #cell-user="{ data }">
						<div class="voter-row">
							<div class="persona">
								<ProfilePic :user="data" size="40px" />
								<div class="voter-name" v-text="data.fullname" />
							</div>
						</div>
					</template>
				</Table>
			</div>
		</StandardModal>
	</StandardModal>
</template>

<script setup>
import { ref, onMounted, defineEmits, computed, watch } from 'vue'
import {
	StandardModal,
	Input,
	Accordian,
	ProfilePic,
	Table,
	TranslationGlobe,
} from '@/components/common'
import { SearchIcon } from '@/icons'
import { searchPollVoters, getVotersForOption, translatePoll } from '@/api/poll'
import { showUserModal } from '@/helpers/utils'
import { useI18n } from 'vue-i18n'
import { getPoll, updatePoll } from '@/store/messages'
import { getMyUser } from '@/store'
import * as translations from '@/store/translations'
import debounce from 'debounce'
import PollCloseState from '@/components/Polls/PollCloseState'
import { useTemplateRef } from 'vue'
import { normalizeUser } from '@/store/memberships'

const props = defineProps({
	pollId: {
		type: [Number, String],
		required: true,
	},
})

const emit = defineEmits(['close'])
const { t } = useI18n()

// Reactive state
const isLoading = ref(false)
const isTranslating = ref(false)
const query = ref('')
const showAllVotersModal = ref(false)
const selectedOption = ref(null)
const poll = ref(null)
const apiResponse = ref(null)
const votersColumns = ref([{ field: 'user' }])
const allVoters = ref([])
const loadingMoreVoters = ref(false)
const noResults = ref(false)
const modal = useTemplateRef('modal')

// Computed
const isAnonymous = computed(
	() => poll.value?.chat_voter_visibility === 'system'
)

const canTranslate = computed(() => {
	if (!poll.value?.language) return false
	const me = getMyUser()
	const myLangs = [me.language, ...(me.languages || [])]
	return !myLangs.includes(poll.value.language)
})

const displayedQuestion = computed(() => {
	if (poll.value?.isTranslated && poll.value?.translations) {
		const me = getMyUser()
		const translation = poll.value.translations.find(
			(t) => t.language === me.language
		)
		return translation?.translation || poll.value?.question
	}
	return poll.value?.question
})

const isOptionTranslated = (option) => {
	if (!poll.value?.isTranslated || !option?.translations) return false
	const me = getMyUser()
	const translation = option.translations.find(
		(t) => t.language === me.language
	)
	return !!translation
}

// Methods
const getDisplayedOptionText = (option) => {
	if (poll.value?.isTranslated && option?.translations) {
		const me = getMyUser()
		const translation = option.translations.find(
			(t) => t.language === me.language
		)
		return translation?.translation || option.text
	}
	return option?.text
}

const onGlobeClick = async () => {
	if (isTranslating.value) return
	isTranslating.value = true

	if (translations.check(props.pollId, false, true)) {
		translations.remove(props.pollId, false, true)
	} else {
		translations.add(props.pollId, false, true)
	}

	try {
		const translatedPoll = await translatePoll(props.pollId)
		poll.value = translatedPoll
	} catch (error) {
		console.error('Translation failed:', error)
		modal.value?.$alert.error(error)
	} finally {
		isTranslating.value = false
	}
}

// Lifecycle hooks
onMounted(async () => {
	poll.value = getPoll(props.pollId)

	// Check Poll for translation state
	if (canTranslate.value && poll.value?.translations?.length) {
		const me = getMyUser()
		const hasManualOverride = translations.check(props.pollId, false, true)
		const shouldTranslate = hasManualOverride
			? !me.autotranslate
			: me.autotranslate

		if (poll.value?.isTranslated !== shouldTranslate) {
			poll.value = updatePoll(props.pollId, { isTranslated: shouldTranslate })
		}
	}

	// Fetch voters separately (skip for anonymous polls)
	if (!isAnonymous.value) {
		await fetchPollVoters()
	}
})

const fetchPollVoters = async () => {
	try {
		isLoading.value = true
		noResults.value = false
		const params = query.value ? { 'filter[name]': query.value } : {}
		const votersResponse = await searchPollVoters(props.pollId, params)

		// Merge voters data into poll options
		if (votersResponse?.data && poll.value?.options) {
			const votersMap = new Map()
			votersResponse.data.forEach((item) => {
				votersMap.set(item.option_id, {
					voters: item.voters.map((user) => normalizeUser(user)),
					has_more: item.has_more,
				})
			})

			poll.value.options.forEach((option) => {
				const voterData = votersMap.get(option.id)
				if (voterData) {
					option.voters = voterData.voters
					option.has_more = voterData.has_more
				} else {
					option.voters = []
					option.has_more = false
				}
			})

			if (
				query.value &&
				votersResponse.data.every((item) => item.voters.length === 0)
			) {
				noResults.value = true
			}
		}
	} catch (error) {
		console.error('Error fetching poll voters:', error)
		modal.value?.$alert.error(error)
	} finally {
		isLoading.value = false
	}
}

const debouncedFetchPollVoters = debounce(fetchPollVoters, 250)

const totalVotes = computed(() =>
	poll.value?.options?.reduce(
		(sum, option) => sum + (option.vote_count || 0),
		0
	)
)

const getVotePercentage = (option) => {
	const optionVotes = option.vote_count || 0
	if (totalVotes.value === 0) return 0
	return Math.round((optionVotes / totalVotes.value) * 1000) / 10
}

const onClickUser = (user) => {
	showUserModal(user.id)
}

const openAllVotersModal = async (option) => {
	selectedOption.value = option
	showAllVotersModal.value = true
	const response = await getVotersForOption(props.pollId, option.id)
	apiResponse.value = response
	allVoters.value = response.data.map((user) => normalizeUser(user))
}

const loadMore = async () => {
	if (!apiResponse.value?.next) return
	try {
		loadingMoreVoters.value = true
		apiResponse.value = await apiResponse.value.next()
		allVoters.value = allVoters.value.concat(
			apiResponse.value.data.map((user) => normalizeUser(user))
		)
	} catch (e) {
		console.error(e)
		modal.value?.$alert.error(e)
	}
	loadingMoreVoters.value = false
}

// Add watcher to refetch when search query changes (not for anonymous polls)
watch(query, () => {
	if (isAnonymous.value) return
	isLoading.value = true
	debouncedFetchPollVoters()
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.poll-results-modal {
	.poll-results-title-container {
		.poll-results-title {
			color: var(--neutral-800);
			max-width: 100%;
			overflow: hidden;
			font-size: 26px;
			font-family: Avenir-Black;
			padding-top: 12px;
			padding-bottom: 2px;
			word-wrap: break-word;
		}
		.poll-results-subtitle {
			display: flex;
			align-items: center;
			gap: 6px;
			font-size: 13px;
			font-family: Avenir-Medium;
			color: var(--neutral-600);

			.pronto-icon {
				font-size: 12px;
				flex-shrink: 0;
			}
		}
	}

	.poll-results-container {
		display: flex;
		flex-direction: column;
		padding: 24px 64px;
		gap: 12px;

		.poll-question-text-container {
			width: 100%;
			&.translated {
				background-color: rgba(73, 164, 218, 0.15);
				border-radius: 6px;
				padding: 4px 6px;
				margin: -4px -6px;
			}
		}

		.poll-question {
			padding: 8px 16px;
			border-radius: 6px;
			border: 1px solid var(--neutral-200);
			background: var(--neutral-100);

			.poll-question-text {
				font-size: 16px;
				font-family: Avenir-Heavy;
				color: var(--neutral-800);
				word-wrap: break-word;
				flex: 1;
			}

			.poll-translation {
				font-size: 12px;
				font-family: Avenir-Roman;
				color: var(--neutral-600);
				vertical-align: bottom;
				cursor: pointer;

				&:hover {
					color: var(--neutral-700);
				}
			}
		}

		.poll-voters-accordian {
			border-radius: 6px;
			border: 1px solid var(--neutral-200);
			background: var(--neutral-100);
			padding: 13px 16px;

			:deep(.content) {
				margin-top: 0;
			}

			:deep(.title-container) {
				.title {
					font-size: 16px;
				}
			}

			:deep(.chevron-icon) {
				font-size: 16px;
				color: var(--neutral-600);
			}

			.poll-option-title {
				max-width: max-content;
				&.translated {
					background-color: rgba(73, 164, 218, 0.15);
					border-radius: 6px;
					padding: 4px 6px;
				}
			}

			.poll-voters-list {
				max-height: 550px;
				padding-top: 8px;

				.voter-item {
					display: flex;
					align-items: center;
					padding: 8px 0px;
					cursor: pointer;
					transition: background-color 0.2s ease;

					&:only-child {
						padding-bottom: 0;
					}

					&:first-child {
						border-top: 1px solid var(--neutral-200);
					}

					&:not(:last-child) {
						border-bottom: 1px solid var(--neutral-200);
					}

					&:hover {
						background-color: var(--neutral-100);
					}

					.voter-profile {
						flex-shrink: 0;
						margin-right: 12px;
					}

					.voter-name {
						font-size: 16px;
						font-family: Avenir-Roman;
						color: var(--neutral-800);
						overflow: hidden;
						white-space: nowrap;
						text-overflow: ellipsis;
						flex: 1;
					}
				}

				.view-all-votes {
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 16px;
					font-family: Avenir-Roman;
					color: var(--blue-300);
					padding: 19px 0 0 0;
					border: none !important;
				}
			}
		}

		.poll-option-section {
			margin-bottom: 16px;

			&:last-child {
				margin-bottom: 0;
			}

			&.hidden {
				display: none;
			}

			.vote-stats {
				display: flex;
				height: 16px;
				.vote-count {
					padding-right: 8px;
				}
				.vote-percentage {
					border-left: 1px solid var(--neutral-200);
					padding-left: 8px;
				}
			}
		}

		.poll-results-no-results {
			font-size: 17px;
			font-family: Avenir-Roman;
			color: var(--neutral-600);
			height: 40vh;
			display: flex;
			align-items: center;
			justify-content: center;
		}
	}
}

.all-voters-modal {
	.all-voters-container {
		.voter-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 8px 0px;
			font-family: 'Avenir-Book';
			font-size: 16px;

			.persona {
				display: flex;
				align-items: center;
				min-width: 0;

				.voter-name {
					margin-left: 12px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					max-width: 400px;
				}
			}
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.poll-results-modal {
	.modal-content {
		height: 90vh !important;
	}
}

@include overrideScheme('dark') {
	.create-poll-modal .pronto-radio-group {
		background: var(--neutral-200);
	}
}
</style>
